const DB_NAME = "spinesim-report";
const DB_VERSION = 2;
const STORE_NAME = "report-records";

const memoryStore = new Map();

const hasIndexedDbSupport = () =>
  typeof window !== "undefined" &&
  typeof window.indexedDB !== "undefined" &&
  typeof window.indexedDB.open === "function";

const openDatabase = () =>
  new Promise((resolve, reject) => {
    if (!hasIndexedDbSupport()) {
      resolve(null);
      return;
    }

    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };

      request.onsuccess = event => {
        resolve(event.target.result);
      };

      request.onerror = event => {
        reject(event.target.error || new Error("Failed to open IndexedDB"));
      };
    } catch (error) {
      reject(error);
    }
  });

const runTransaction = (db, mode, executor) =>
  new Promise((resolve, reject) => {
    if (!db) {
      try {
        resolve(executor(null));
      } catch (error) {
        reject(error);
      }
      return;
    }

    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);

    transaction.oncomplete = () => {
      db.close();
    };

    transaction.onerror = event => {
      reject(event.target.error || new Error("IndexedDB transaction failed"));
    };

    Promise.resolve(executor(store))
      .then(resolve)
      .catch(reject);
  });

export const saveReportRecord = async record => {
  if (!record || !record.id) {
    throw new Error("saveReportRecord: record with id is required");
  }

  const db = await openDatabase();

  if (!db) {
    memoryStore.set(record.id, record);
    return {
      record,
      storage: "memory",
    };
  }

  return runTransaction(db, "readwrite", store => {
    return new Promise((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () =>
        resolve({
          record,
          storage: "indexeddb",
        });
      request.onerror = event => reject(event.target.error);
    });
  });
};

export const getReportRecordsBySession = async sessionId => {
  if (!sessionId) {
    return [];
  }

  const db = await openDatabase();

  if (!db) {
    return Array.from(memoryStore.values()).filter(
      item => item.sessionId === sessionId
    );
  }

  return runTransaction(db, "readonly", store => {
    return new Promise((resolve, reject) => {
      const records = [];
      const request = store.openCursor();

      request.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          const value = cursor.value;
          if (value && value.sessionId === sessionId) {
            records.push(value);
          }
          cursor.continue();
        } else {
          resolve(records);
        }
      };

      request.onerror = event => reject(event.target.error);
    });
  });
};

export const deleteReportRecord = async id => {
  if (!id) {
    return;
  }

  const db = await openDatabase();

  if (!db) {
    memoryStore.delete(id);
    return;
  }

  return runTransaction(db, "readwrite", store => {
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = event => reject(event.target.error);
    });
  });
};

export const clearAllReportRecords = async () => {
  const db = await openDatabase();

  if (!db) {
    memoryStore.clear();
    return;
  }

  return runTransaction(db, "readwrite", store => {
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = event => reject(event.target.error);
    });
  });
};

export const deleteReportRecordsNotInSession = async sessionId => {
  if (!sessionId) {
    await clearAllReportRecords();
    return;
  }

  const db = await openDatabase();

  if (!db) {
    for (const [key, value] of memoryStore.entries()) {
      if (value.sessionId !== sessionId) {
        memoryStore.delete(key);
      }
    }
    return;
  }

  await runTransaction(db, "readwrite", store => {
    return new Promise((resolve, reject) => {
      const request = store.openCursor();

      request.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          const value = cursor.value;
          if (value && value.sessionId !== sessionId) {
            store.delete(cursor.primaryKey);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = event => reject(event.target.error);
    });
  });
};

export const deleteReportRecordsBySession = async sessionId => {
  if (!sessionId) {
    return;
  }

  const db = await openDatabase();

  if (!db) {
    for (const [key, value] of memoryStore.entries()) {
      if (value.sessionId === sessionId) {
        memoryStore.delete(key);
      }
    }
    return;
  }

  await runTransaction(db, "readwrite", store => {
    return new Promise((resolve, reject) => {
      const request = store.openCursor();

      request.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          const value = cursor.value;
          if (value && value.sessionId === sessionId) {
            store.delete(cursor.primaryKey);
          }
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = event => reject(event.target.error);
    });
  });
};
